import { Badge } from '@/shared/components/Badge'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import SelectBuscable from '@/shared/components/SelectBuscable'
import {
  Upload, FileText, PencilLine, Trash2, CheckCircle, XCircle,
  ArrowLeft, PlusCircle, Eye, MessageSquare, X
} from 'lucide-react'
import { apiService } from '@/services/api'
import { Notification, useNotification } from '@/shared/components/Notification'
import ConfirmActionModal from '@/shared/components/ConfirmActionModal'
import {
  MEXICAN_STATES, US_STATES, US_CITIES, SAT_FISCAL_REGIMES,
  SAT_CFDI_USAGE, MEXICAN_BANKS, SERVICE_TYPES
} from '@/constants/supplierConstants'
import { formatDate } from '@/shared/lib/dateUtils'
import { config } from '@/config/env'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface EditSupplierFormProps {
  supplierId: string
  onBack: () => void
  onSuccess?: () => void
}

type FormStep = 1 | 2 | 3 | 4

interface LocalDocument {
  documentTypeId: string
  documentTypeName: string
  file: File
  fileName: string
}

interface ExistingDocument {
  id: string
  documentTypeId: string
  documentTypeName: string
  fileName: string
  fileUrl: string
  status: string
  uploadedAt: string
  reviewComment: string | null
  hasComments: boolean
}

interface DocComment {
  id: string
  comment: string
  createdAt: string
  user?: { firstName?: string; lastName?: string; email?: string }
}

interface FormData {
  razonSocial: string
  nombreComercial: string
  tipoServicio: string
  contactoPrincipal: string
  telefono: string
  correoVentas: string
  correoCuentas: string
  // Días de crédito acordados (1-60). Cadena porque viene de un input.
  diasCredito: string
  rfc: string
  tipoProveedor: string
  regimenFiscal: string
  usoCFDI: string
  correoFiscal: string
  telefonoFiscal: string
  pais: string
  estado: string
  ciudad: string
  municipio: string
  calle: string
  numero: string
  colonia: string
  cp: string
  clabe: string
  banco: string
  nombreCuenta: string
  numeroCuenta: string
  moneda: string
  customBankName: string
  contactFirstName: string
  contactLastName: string
  email: string
  isActive: boolean
}

const EditSupplierForm = ({ supplierId, onBack, onSuccess }: EditSupplierFormProps) => {
  const [currentStep, setCurrentStep] = useState<FormStep>(1)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [localDocuments, setLocalDocuments] = useState<LocalDocument[]>([])
  const [_existingDocuments, setExistingDocuments] = useState<ExistingDocument[]>([])
  const [docRows, setDocRows] = useState<{ docTypeId: string; docTypeName: string; description: string | null; isRequired: boolean; allowedMimeTypes?: string[]; existingDoc: ExistingDocument | null }[]>([])
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showExitModal, setShowExitModal] = useState(false)
  const [availableColonias, setAvailableColonias] = useState<string[]>([])
  const [savingProgress, setSavingProgress] = useState<string>('')
  const [bankFile, setBankFile] = useState<File | null>(null)
  const [existingBankCoverUrl, setExistingBankCoverUrl] = useState<string | null>(null)
  const [_existingBankCoverOriginalName, setExistingBankCoverOriginalName] = useState<string | null>(null)
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false)
  const [commentingDocId, setCommentingDocId] = useState<string | null>(null)
  const [commentText, setCommentText] = useState('')
  const [commentsHistory, setCommentsHistory] = useState<DocComment[]>([])
  const [isLoadingComments, setIsLoadingComments] = useState(false)
  const [isSavingComment, setIsSavingComment] = useState(false)

  const { notification, showSuccess, showError, hideNotification } = useNotification()

  const {
    register,
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    trigger,
    reset
  } = useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      razonSocial: '', nombreComercial: '', tipoServicio: '',
      contactoPrincipal: '', telefono: '', correoVentas: '', correoCuentas: '',
      rfc: '', tipoProveedor: '', regimenFiscal: '', usoCFDI: '',
      correoFiscal: '', telefonoFiscal: '',
      pais: '', estado: '', ciudad: '', municipio: '',
      calle: '', numero: '', colonia: '', cp: '',
      clabe: '', banco: '', nombreCuenta: '', numeroCuenta: '', moneda: 'MXN', customBankName: '',
      contactFirstName: '', contactLastName: '', email: '',
      isActive: true
    }
  })

  const formData = watch()
  const hasUnsavedData = isDirty || localDocuments.length > 0 || bankFile !== null
  const normalizeTenDigitPhone = (value?: string | null): string => {
    const digits = (value || '').replace(/\D/g, '')
    return digits.length >= 10 ? digits.slice(0, 10) : digits
  }

  // Warn on browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedData) { e.preventDefault() }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedData])

  const handleBack = () => {
    if (hasUnsavedData) { setShowExitModal(true) } else { onBack() }
  }

  // Load supplier data
  useEffect(() => {
    const loadSupplierData = async () => {
      try {
        setIsLoading(true)
        const data = await apiService.request<{
          general: {
            id: string; rfc: string; legalName: string; commercialName: string | null;
            serviceTypeId: string; country: string; state: string; city: string;
            municipality: string; suburb: string; postalCode: string; street: string;
            externalNumber: string; mainContactName: string; mainContactPhone: string;
            salesEmail: string; accountsPayableEmail: string; status: string;
            creditDays?: number | null;
          }
          fiscal: { taxRegime: string | null; cfdiUse: string | null; taxEmail: string | null; taxPhone: string | null } | null
          primaryBankAccount: { id: string; accountHolderName: string | null; bankName: string; clabe: string; accountNumber?: string | null; currency: string; coverUrl: string | null; coverOriginalName: string | null } | null
          user?: { firstName: string; lastName: string; email: string; isActive: boolean }
        }>(`/internal/suppliers/${supplierId}`)

        const knownBank = MEXICAN_BANKS.find(b => b.name === data.primaryBankAccount?.bankName)
        const contactoTelefono = normalizeTenDigitPhone(
          data.general.mainContactPhone || data.fiscal?.taxPhone || ''
        )
        const fiscalTelefono = normalizeTenDigitPhone(
          data.fiscal?.taxPhone || data.general.mainContactPhone || ''
        )

        reset({
          razonSocial: data.general.legalName || '',
          nombreComercial: data.general.commercialName || '',
          tipoServicio: data.general.serviceTypeId || '',
          contactoPrincipal: data.general.mainContactName || '',
          telefono: contactoTelefono,
          correoVentas: data.general.salesEmail || '',
          correoCuentas: data.general.accountsPayableEmail || '',
          diasCredito: data.general.creditDays != null ? String(data.general.creditDays) : '',
          rfc: data.general.rfc || '',
          tipoProveedor: '',
          regimenFiscal: data.fiscal?.taxRegime || '',
          usoCFDI: data.fiscal?.cfdiUse || '',
          correoFiscal: data.fiscal?.taxEmail || '',
          telefonoFiscal: fiscalTelefono,
          pais: data.general.country || '',
          estado: data.general.state || '',
          ciudad: data.general.city || '',
          municipio: data.general.municipality || '',
          calle: data.general.street || '',
          numero: data.general.externalNumber || '',
          colonia: data.general.suburb || '',
          cp: data.general.postalCode || '',
          clabe: data.primaryBankAccount?.clabe || '',
          banco: knownBank ? knownBank.name : (data.primaryBankAccount?.bankName ? 'Otro' : ''),
          nombreCuenta: data.primaryBankAccount?.accountHolderName || '',
          numeroCuenta: data.primaryBankAccount?.accountNumber || '',
          moneda: data.primaryBankAccount?.currency || 'MXN',
          customBankName: knownBank ? '' : (data.primaryBankAccount?.bankName || ''),
          contactFirstName: data.user?.firstName || '',
          contactLastName: data.user?.lastName || '',
          email: data.user?.email || '',
          isActive: data.user?.isActive ?? true
        })

        if (data.primaryBankAccount?.coverUrl) {
          setExistingBankCoverUrl(data.primaryBankAccount.coverUrl)
        }
        if (data.primaryBankAccount?.coverOriginalName) {
          setExistingBankCoverOriginalName(data.primaryBankAccount.coverOriginalName)
        }
      } catch (err) {
        console.error('Error loading supplier:', err)
        showError('Error', 'No se pudo cargar la información del proveedor')
      } finally {
        setIsLoading(false)
      }
    }
    loadSupplierData()
  }, [supplierId])

  // Load uploaded documents from dedicated endpoint
  const loadSupplierDocuments = async () => {
    try {
      const docs = await apiService.request<Array<{
        id: string
        documentTypeId: string
        fileUrl: string
        fileName: string
        version: number
        status: string
        uploadedAt: string
        reviewComment: string | null
        hasComments: boolean
        documentType: { id: string; name: string; description: string | null; isRequired: boolean; allowedMimeTypes?: string[] }
      }>>(`/internal/suppliers/${supplierId}/documents`)

      if (!Array.isArray(docs)) { setExistingDocuments([]); setDocRows([]); return }

      // Group by documentTypeId and keep only the highest version for each
      const latestByType = new Map<string, typeof docs[0]>()
      for (const doc of docs) {
        const current = latestByType.get(doc.documentTypeId)
        if (!current || doc.version > current.version) {
          latestByType.set(doc.documentTypeId, doc)
        }
      }

      const existingList = Array.from(latestByType.values()).map(d => ({
        id: d.id,
        documentTypeId: d.documentTypeId,
        documentTypeName: d.documentType.name,
        fileName: d.fileName,
        fileUrl: d.fileUrl,
        status: d.status,
        uploadedAt: d.uploadedAt || '',
        reviewComment: d.reviewComment || null,
        hasComments: d.hasComments ?? false
      }))
      setExistingDocuments(existingList)

      // Build doc rows from the unique document types
      const rows = Array.from(latestByType.values()).map(d => ({
        docTypeId: d.documentType.id,
        docTypeName: d.documentType.name,
        description: d.documentType.description,
        isRequired: d.documentType.isRequired,
        allowedMimeTypes: d.documentType.allowedMimeTypes,
        existingDoc: existingList.find(ed => ed.documentTypeId === d.documentTypeId) || null
      }))
      setDocRows(rows)
    } catch {
      setExistingDocuments([])
      setDocRows([])
    }
  }

  useEffect(() => {
    loadSupplierDocuments()
  }, [supplierId])

  // Postal code auto-fill
  const watchedCp = watch('cp')
  const watchedPais = watch('pais')

  useEffect(() => {
    const fetchPostalCodeData = async () => {
      if (watchedPais === 'MX' && watchedCp && watchedCp.length === 5 && /^\d+$/.test(watchedCp)) {
        try {
          const response = await fetch(`${config.apiBaseUrl}/catalogos/postal-codes/${watchedCp}`)
          if (response.ok) {
            const data = await response.json()
            if (data.municipio) setValue('municipio', data.municipio, { shouldValidate: true })
            if (data.estado) {
              const stateObj = MEXICAN_STATES.find(s => s.name.toLowerCase() === data.estado.toLowerCase() || s.name === data.estado)
              if (stateObj) setValue('estado', stateObj.code, { shouldValidate: true })
            }
            if (data.colonias && Array.isArray(data.colonias) && data.colonias.length > 0) {
              setAvailableColonias(data.colonias)
              if (data.colonias.length === 1) {
                setValue('colonia', data.colonias[0], { shouldValidate: true })
              } else {
                const currentColonia = watch('colonia')
                if (currentColonia && !data.colonias.includes(currentColonia)) {
                  setValue('colonia', '', { shouldValidate: true })
                }
              }
            } else { setAvailableColonias([]) }
          }
        } catch { setAvailableColonias([]) }
      } else {
        if (watchedPais !== 'MX' || !watchedCp || watchedCp.length < 5) { setAvailableColonias([]) }
      }
    }
    fetchPostalCodeData()
  }, [watchedCp, watchedPais, setValue])

  // Local document handling
  const handleLocalFileAdd = (documentTypeId: string, documentTypeName: string, file: File) => {
    setLocalDocuments(prev => {
      const filtered = prev.filter(d => d.documentTypeId !== documentTypeId)
      return [...filtered, { documentTypeId, documentTypeName, file, fileName: file.name }]
    })
  }

  const handleLocalFileRemove = (documentTypeId: string) => {
    setLocalDocuments(prev => prev.filter(d => d.documentTypeId !== documentTypeId))
  }

  const handleFileInputChange = (documentTypeId: string, documentTypeName: string) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleLocalFileAdd(documentTypeId, documentTypeName, file)
    }

  // Comments
  const fetchDocumentComments = async (docId: string) => {
    try {
      setIsLoadingComments(true)
      const token = localStorage.getItem('access_token')
      const response = await fetch(`${config.apiBaseUrl}/admin/suppliers/documents/${docId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setCommentsHistory(data || [])
      } else { setCommentsHistory([]) }
    } catch { setCommentsHistory([]) }
    finally { setIsLoadingComments(false) }
  }

  const handleOpenComment = (docId: string) => {
    setCommentingDocId(docId)
    setCommentText('')
    setIsCommentModalOpen(true)
    fetchDocumentComments(docId)
  }

  const handleSaveComment = async () => {
    if (!commentingDocId || !commentText.trim()) return
    try {
      setIsSavingComment(true)
      const token = localStorage.getItem('access_token')
      const response = await fetch(`${config.apiBaseUrl}/admin/suppliers/documents/${commentingDocId}/comments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: commentText })
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Error al guardar el comentario')
      }
      showSuccess('¡Éxito!', 'Comentario guardado correctamente')
      setCommentText('')
      await fetchDocumentComments(commentingDocId)
      await loadSupplierDocuments()
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'No se pudo guardar el comentario'
      showError('Error', msg)
    } finally { setIsSavingComment(false) }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved': return <Badge color="verde">Aprobado</Badge>
      case 'rejected': return <Badge color="rojo">Rechazado</Badge>
      case 'observed': return <Badge color="naranja">Observado</Badge>
      default: return <Badge color="amarillo">Pendiente</Badge>
    }
  }

  // Step validation
  const validateStep = async (step: number): Promise<boolean> => {
    let fieldsToValidate: (keyof FormData)[] = []
    switch (step) {
      case 1:
        fieldsToValidate = ['razonSocial', 'tipoServicio', 'contactoPrincipal', 'telefono', 'correoVentas']
        break
      case 2: {
        const isOtro = watch('pais') === 'OTRO'
        fieldsToValidate = ['rfc', 'regimenFiscal', 'usoCFDI', 'correoFiscal', 'telefonoFiscal', 'pais', 'cp', 'estado', 'calle']
        if (!isOtro) { fieldsToValidate.push('municipio', 'colonia', 'numero') }
        break
      }
      case 3:
        fieldsToValidate = ['clabe', 'banco', 'nombreCuenta']
        if (watch('banco') === 'Otro') { fieldsToValidate.push('customBankName') }
        // Validar que la carátula bancaria esté presente
        if (!bankFile && !existingBankCoverUrl) {
          showError('Carátula requerida', 'Debes adjuntar la carátula bancaria para continuar.')
          return false
        }
        break
      case 4:
        return true
    }
    if (fieldsToValidate.length > 0) { return await trigger(fieldsToValidate) }
    return true
  }

  // Save changes
  const handleSave = async () => {
    setIsSaving(true)
    setShowConfirmModal(false)

    try {
      // 1. Save general data
      setSavingProgress('Guardando datos generales...')
      await apiService.request(`/admin/suppliers/${supplierId}`, {
        method: 'PUT',
        body: JSON.stringify({
          legalName: formData.razonSocial,
          commercialName: formData.nombreComercial,
          serviceTypeId: formData.tipoServicio,
          mainContactName: formData.contactoPrincipal,
          mainContactPhone: formData.telefono,
          salesEmail: formData.correoVentas,
          accountsPayableEmail: formData.correoCuentas,
          // Se omite si viene vacío: el back lo valida 1-60 y no acepta null.
          ...(formData.diasCredito.trim() !== '' && {
            creditDays: Number(formData.diasCredito),
          })
        })
      })

      // 2. Save fiscal data
      setSavingProgress('Guardando información fiscal...')
      await apiService.request(`/admin/suppliers/${supplierId}/fiscal`, {
        method: 'PUT',
        body: JSON.stringify({
          taxRegime: formData.regimenFiscal,
          cfdiUse: formData.usoCFDI,
          taxEmail: formData.correoFiscal,
          taxPhone: formData.telefonoFiscal,
          rfc: formData.rfc
        })
      })

      // 3. Save address
      const isOtro = formData.pais === 'OTRO'
      await apiService.request(`/admin/suppliers/${supplierId}`, {
        method: 'PUT',
        body: JSON.stringify({
          country: formData.pais,
          state: formData.estado,
          city: isOtro ? '' : formData.municipio,
          municipality: isOtro ? '' : formData.municipio,
          postalCode: formData.cp,
          street: formData.calle,
          externalNumber: isOtro ? '' : formData.numero,
          suburb: isOtro ? '' : formData.colonia
        })
      })

      // 4. Save bank data
      setSavingProgress('Guardando información bancaria...')
      const bankName = formData.banco === 'Otro' ? formData.customBankName : formData.banco
      const token = localStorage.getItem('access_token')

      if (bankFile) {
        const bankFormData = new FormData()
        bankFormData.append('bankName', bankName)
        bankFormData.append('clabe', formData.clabe)
        if (formData.numeroCuenta) bankFormData.append('accountNumber', formData.numeroCuenta)
        bankFormData.append('currency', formData.moneda || 'MXN')
        bankFormData.append('accountHolderName', formData.nombreCuenta)
        bankFormData.append('file', bankFile)

        const bankResponse = await fetch(
          `${config.apiBaseUrl}/admin/suppliers/${supplierId}/bank-account`,
          { method: 'POST', body: bankFormData, headers: { 'Authorization': `Bearer ${token}` } }
        )
        if (!bankResponse.ok) {
          const errorData = await bankResponse.json().catch(() => ({}))
          throw new Error(errorData.message || 'Error al guardar información bancaria')
        }
      } else {
        await apiService.request(`/admin/suppliers/${supplierId}/bank-account`, {
          method: 'POST',
          body: JSON.stringify({
            bankName,
            clabe: formData.clabe,
            accountNumber: formData.numeroCuenta || undefined,
            currency: formData.moneda || 'MXN',
            accountHolderName: formData.nombreCuenta
          })
        })
      }

      // 5. Upload new documents
      if (localDocuments.length > 0) {
        setSavingProgress('Subiendo documentos...')
        for (const doc of localDocuments) {
          const docFormData = new FormData()
          docFormData.append('documentTypeId', doc.documentTypeId)
          docFormData.append('file', doc.file)

          await fetch(
            `${config.apiBaseUrl}/admin/suppliers/${supplierId}/documents`,
            { method: 'POST', body: docFormData, headers: { 'Authorization': `Bearer ${token}` } }
          )
        }
      }

      setSavingProgress('')
      showSuccess('¡Éxito!', 'Información del proveedor actualizada correctamente')
      setTimeout(() => {
        if (onSuccess) onSuccess()
        else onBack()
      }, 1500)
    } catch (error: unknown) {
      setSavingProgress('')
      const errorMessage = error instanceof Error ? error.message : 'Ocurrió un error al guardar'
      showError('Error', errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  // Navigation
  const handleNext = async () => {
    const isValid = await validateStep(currentStep)
    if (!isValid) {
      showError('Falta información', 'Por favor completa todos los campos requeridos de la pestaña actual.')
      return
    }
    if (currentStep === 4) { setShowConfirmModal(true); return }
    setCurrentStep((currentStep + 1) as FormStep)
  }

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep((currentStep - 1) as FormStep)
  }

  const steps = [
    { number: 1, label: 'Datos generales' },
    { number: 2, label: 'Información fiscal' },
    { number: 3, label: 'Información bancaria' },
    { number: 4, label: 'Documentos requeridos' }
  ]

  const fieldClass = (fieldName: keyof FormData) =>
    `w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${
      errors[fieldName]
        ? 'border-red-500 bg-red-50 focus:border-red-500'
        : watch(fieldName)
          ? 'border-green-500 bg-green-50 focus:border-green-500'
          : 'border-gray-300 bg-gray-50 focus:border-blue-500'
    }`

  const FieldIcon = ({ fieldName }: { fieldName: keyof FormData }) => {
    if (!watch(fieldName)) return null
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2">
        {errors[fieldName] ? <XCircle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-green-500" />}
      </div>
    )
  }

  const FieldMessage = ({ fieldName }: { fieldName: keyof FormData }) => (
    <>
      {errors[fieldName] && <p className="text-xs text-red-600 mt-1">{errors[fieldName]?.message as string}</p>}
      {!errors[fieldName] && watch(fieldName) && <p className="text-xs text-green-600 mt-1">✓ Válido</p>}
    </>
  )

  if (isLoading) {
    return (
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando información del proveedor...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Notification type={notification.type} title={notification.title} message={notification.message} isVisible={notification.isVisible} onClose={hideNotification} />
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <button onClick={handleBack} className={estiloDeBoton('secundario', 'mb-4')}>
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Editar expediente del proveedor</h1>
          <p className="text-sm text-gray-600 mt-1">Revisa y actualiza la información del proveedor</p>
        </div>

        {/* Steps Navigation */}
        <div className="flex gap-3 mb-8">
          {steps.map((step) => (
            <button
              key={step.number}
              onClick={() => setCurrentStep(step.number as FormStep)}
              className={`flex-1 px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                currentStep === step.number
                  ? 'text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
              }`}
              style={currentStep === step.number ? { backgroundColor: '#315FB9' } : {}}
            >
              {step.number}. {step.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">{steps[currentStep - 1].label}</h2>

          {/* Step 1: Datos generales */}
          {currentStep === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Razón social <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('razonSocial', { required: 'La razón social es requerida', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="------" className={fieldClass('razonSocial')} />
                  <FieldIcon fieldName="razonSocial" />
                </div>
                <FieldMessage fieldName="razonSocial" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre comercial</label>
                <div className="relative">
                  <input type="text" {...register('nombreComercial', { minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Ingresa el nombre comercial" className={fieldClass('nombreComercial')} />
                  <FieldIcon fieldName="nombreComercial" />
                </div>
                <FieldMessage fieldName="nombreComercial" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de servicio <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Controller name="tipoServicio" control={control} rules={{ required: 'El tipo de servicio es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="tipoServicio" name="tipoServicio" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.tipoServicio)} placeholder="Seleccionar tipo de servicio" placeholderDeBusqueda="Buscar tipo de servicio…" opciones={SERVICE_TYPES.map((s) => ({ value: s.id, label: s.name }))} />
                  )} />
                </div>
                <FieldMessage fieldName="tipoServicio" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contacto principal <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('contactoPrincipal', { required: 'El contacto principal es requerido', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Nombre del contacto" className={fieldClass('contactoPrincipal')} />
                  <FieldIcon fieldName="contactoPrincipal" />
                </div>
                <FieldMessage fieldName="contactoPrincipal" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('telefono', { required: 'El teléfono es requerido', pattern: { value: /^\d{10}$/, message: 'Debe contener exactamente 10 dígitos' } })} placeholder="5512345678" maxLength={10} className={fieldClass('telefono')} />
                  <FieldIcon fieldName="telefono" />
                </div>
                <FieldMessage fieldName="telefono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email de Ventas <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="email" {...register('correoVentas', { required: 'El email de ventas es requerido', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email no válido' } })} placeholder="ventas@ejemplo.com" className={fieldClass('correoVentas')} />
                  <FieldIcon fieldName="correoVentas" />
                </div>
                <FieldMessage fieldName="correoVentas" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email de Cuentas por Pagar</label>
                <div className="relative">
                  <input type="email" {...register('correoCuentas', { pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email no válido' } })} placeholder="cuentas@ejemplo.com" className={fieldClass('correoCuentas')} />
                  <FieldIcon fieldName="correoCuentas" />
                </div>
                <FieldMessage fieldName="correoCuentas" />
              </div>

              {/* Condiciones de pago — sección propia para que no se pierda
                  entre los datos generales. */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Condiciones de pago</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Días de crédito</label>
                    <div className="relative">
                      <input
                        type="number"
                        min={1}
                        max={60}
                        step={1}
                        {...register('diasCredito', {
                          validate: (value) => {
                            if (!value || value.trim() === '') return true
                            if (!/^\d+$/.test(value.trim())) return 'Debe ser un número entero'
                            const n = Number(value)
                            if (n < 1 || n > 60) return 'Debe estar entre 1 y 60 días'
                            return true
                          },
                        })}
                        placeholder="30"
                        className={fieldClass('diasCredito')}
                      />
                      <FieldIcon fieldName="diasCredito" />
                    </div>
                    <FieldMessage fieldName="diasCredito" />
                    <p className="text-xs text-gray-400 mt-1">
                      Se usa para calcular la fecha de vencimiento de las facturas del proveedor.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Información fiscal + Domicilio */}
          {currentStep === 2 && (
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Información fiscal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">RFC <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="text" {...register('rfc', { required: 'El RFC es requerido', pattern: { value: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/, message: 'RFC inválido' }, onChange: (e) => { e.target.value = e.target.value.toUpperCase() } })} placeholder="AAAA000000000" className={fieldClass('rfc')} />
                      <FieldIcon fieldName="rfc" />
                    </div>
                    <FieldMessage fieldName="rfc" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Régimen fiscal <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Controller name="regimenFiscal" control={control} rules={{ required: 'El régimen fiscal es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="regimenFiscal" name="regimenFiscal" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.regimenFiscal)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar régimen…" opciones={SAT_FISCAL_REGIMES.map((r) => ({ value: r.code, label: `${r.code} - ${r.name}` }))} />
                      )} />
                    </div>
                    <FieldMessage fieldName="regimenFiscal" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Uso de CFDI <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Controller name="usoCFDI" control={control} rules={{ required: 'El uso de CFDI es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="usoCFDI" name="usoCFDI" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.usoCFDI)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar uso de CFDI…" opciones={SAT_CFDI_USAGE.map((u) => ({ value: u.code, label: `${u.code} - ${u.name}` }))} />
                      )} />
                    </div>
                    <FieldMessage fieldName="usoCFDI" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Correo fiscal <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="email" {...register('correoFiscal', { required: 'El correo fiscal es requerido', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Formato de correo inválido' } })} placeholder="Ej. empresa@example.com" className={fieldClass('correoFiscal')} />
                      <FieldIcon fieldName="correoFiscal" />
                    </div>
                    <FieldMessage fieldName="correoFiscal" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="text" {...register('telefonoFiscal', { required: 'El teléfono es requerido', pattern: { value: /^\d{10}$/, message: 'Debe contener exactamente 10 dígitos' } })} placeholder="5512345678" maxLength={10} className={fieldClass('telefonoFiscal')} />
                      <FieldIcon fieldName="telefonoFiscal" />
                    </div>
                    <FieldMessage fieldName="telefonoFiscal" />
                    <p className="text-xs text-gray-500 mt-1">{watch('telefonoFiscal')?.length || 0}/10</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Domicilio fiscal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">País <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Controller name="pais" control={control} rules={{ required: 'El país es requerido' }} render={({ field }) => (
                        <SelectBuscable variante="formulario" id="pais" name="pais" value={field.value ?? ''} onChange={(valor) => { field.onChange(valor); setValue('estado', ''); setValue('municipio', ''); setValue('colonia', ''); setValue('cp', ''); setValue('numero', ''); setAvailableColonias([]) }} error={Boolean(errors.pais)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar país…" opciones={[{ value: 'MX', label: 'México' }, { value: 'US', label: 'Estados Unidos' }, { value: 'OTRO', label: 'OTRO' }]} />
                      )} />
                    </div>
                    <FieldMessage fieldName="pais" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado/Provincia <span className="text-red-500">*</span></label>
                    <div className="relative">
                      {watch('pais') === 'OTRO' ? (
                        <input type="text" {...register('estado', { required: 'El estado/provincia es requerido', minLength: { value: 2, message: 'Mínimo 2 caracteres' } })} placeholder="Ingresa el estado o provincia" className={fieldClass('estado')} />
                      ) : (
                        <Controller name="estado" control={control} rules={{ required: 'El estado es requerido' }} render={({ field }) => (
                          <SelectBuscable variante="formulario" id="estado" name="estado" value={field.value ?? ''} onChange={(valor) => { field.onChange(valor); setValue('municipio', '') }} error={Boolean(errors.estado)} placeholder="Seleccionar" placeholderDeBusqueda="Buscar estado…" opciones={(watch('pais') === 'MX' ? MEXICAN_STATES : watch('pais') === 'US' ? US_STATES : []).map((e) => ({ value: e.code, label: e.name }))} />
                        )} />
                      )}
                      <FieldIcon fieldName="estado" />
                    </div>
                    <FieldMessage fieldName="estado" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CP {watch('pais') !== 'OTRO' ? <span className="text-red-500">*</span> : '(Opcional/ZIP)'}</label>
                    <div className="relative">
                      <input type="text" {...register('cp', { required: watch('pais') !== 'OTRO' ? 'El código postal es requerido' : false, pattern: watch('pais') !== 'OTRO' ? { value: /^\d{5}$/, message: 'Debe contener exactamente 5 dígitos' } : undefined })} placeholder={watch('pais') === 'OTRO' ? 'Código Postal / ZIP' : '12345'} maxLength={watch('pais') === 'OTRO' ? 10 : 5} className={fieldClass('cp')} />
                      <FieldIcon fieldName="cp" />
                    </div>
                    <FieldMessage fieldName="cp" />
                    {watch('pais') !== 'OTRO' && <p className="text-xs text-gray-500 mt-1">{watch('cp')?.length || 0}/5</p>}
                  </div>
                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Municipio <span className="text-red-500">*</span></label>
                      <div className="relative">
                        {watch('pais') === 'US' && watch('estado') && US_CITIES[watch('estado')] ? (
                          <Controller name="municipio" control={control} rules={{ required: 'El municipio es requerido' }} render={({ field }) => (
                            <SelectBuscable variante="formulario" id="municipio" name="municipio" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.municipio)} placeholder="Seleccionar municipio/ciudad" placeholderDeBusqueda="Buscar municipio…" opciones={US_CITIES[watch('estado')].map((c) => ({ value: c, label: c }))} />
                          )} />
                        ) : (
                          <input type="text" {...register('municipio', { required: 'El municipio es requerido', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Ingresa el municipio" className={fieldClass('municipio')} />
                        )}
                        <FieldIcon fieldName="municipio" />
                      </div>
                      <FieldMessage fieldName="municipio" />
                    </div>
                  )}
                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Colonia <span className="text-red-500">*</span></label>
                      <div className="relative">
                        {availableColonias.length > 0 ? (
                          <Controller name="colonia" control={control} rules={{ required: 'La colonia es requerida' }} render={({ field }) => (
                            <SelectBuscable variante="formulario" id="colonia" name="colonia" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.colonia)} placeholder="Seleccionar colonia" placeholderDeBusqueda="Buscar colonia…" opciones={availableColonias.map((c) => ({ value: c, label: c }))} />
                          )} />
                        ) : (
                          <input type="text" {...register('colonia', { required: 'La colonia es requerida', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Ingresa la colonia" className={fieldClass('colonia')} />
                        )}
                        <FieldIcon fieldName="colonia" />
                      </div>
                      <FieldMessage fieldName="colonia" />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{watch('pais') === 'OTRO' ? 'Calle y número' : 'Calle'} <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input type="text" {...register('calle', { required: 'La calle es requerida', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder={watch('pais') === 'OTRO' ? 'Ej. Avenida Principal 412' : 'Ingresa la calle'} className={fieldClass('calle')} />
                      <FieldIcon fieldName="calle" />
                    </div>
                    <FieldMessage fieldName="calle" />
                  </div>
                  {watch('pais') !== 'OTRO' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Número <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input type="text" {...register('numero', { required: 'El número es requerido' })} placeholder="Ingresa el número" className={fieldClass('numero')} />
                        <FieldIcon fieldName="numero" />
                      </div>
                      <FieldMessage fieldName="numero" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Información bancaria */}
          {currentStep === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ paddingBottom: '6.25rem' }}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CLABE <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('clabe', { required: 'La CLABE es requerida', pattern: { value: /^\d{18}$/, message: 'Debe contener exactamente 18 dígitos' } })} placeholder="123456789012345678" maxLength={18} className={fieldClass('clabe')} />
                  <FieldIcon fieldName="clabe" />
                </div>
                <FieldMessage fieldName="clabe" />
                <p className="text-xs text-gray-500 mt-1">{watch('clabe')?.length || 0}/18</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Número de cuenta</label>
                <div className="relative">
                  <input type="text" {...register('numeroCuenta')} placeholder="Ej. 1234567890" className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm ${watch('numeroCuenta') ? 'border-green-500 bg-green-50 focus:border-green-500' : 'border-gray-300 bg-gray-50 focus:border-blue-500'}`} />
                  {watch('numeroCuenta') && <div className="absolute right-3 top-1/2 -translate-y-1/2"><CheckCircle className="w-5 h-5 text-green-500" /></div>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Banco <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Controller name="banco" control={control} rules={{ required: 'El banco es requerido' }} render={({ field }) => (
                    <SelectBuscable variante="formulario" id="banco" name="banco" value={field.value ?? ''} onChange={field.onChange} error={Boolean(errors.banco)} placeholder="Seleccionar banco" placeholderDeBusqueda="Buscar banco…" opciones={[...MEXICAN_BANKS.map((b) => ({ value: b.name, label: b.name })), { value: 'Otro', label: 'Otro' }]} />
                  )} />
                </div>
                <FieldMessage fieldName="banco" />
              </div>
              {watch('banco') === 'Otro' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre del banco <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input type="text" {...register('customBankName', { required: watch('banco') === 'Otro' ? 'El nombre del banco es requerido' : false, minLength: { value: 2, message: 'Mínimo 2 caracteres' } })} placeholder="Nombre del Banco" className={fieldClass('customBankName')} />
                    <FieldIcon fieldName="customBankName" />
                  </div>
                  <FieldMessage fieldName="customBankName" />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de cuenta <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type="text" {...register('nombreCuenta', { required: 'El nombre de cuenta es requerido', minLength: { value: 3, message: 'Mínimo 3 caracteres' } })} placeholder="Nombre del titular de la cuenta" className={fieldClass('nombreCuenta')} />
                  <FieldIcon fieldName="nombreCuenta" />
                </div>
                <FieldMessage fieldName="nombreCuenta" />
              </div>
              {/* Bank file upload */}
              <div className="md:col-span-2">
                {bankFile ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Carátula bancaria</label>
                    <div className="inline-flex flex-col items-start">
                      <div className="relative group">
                        <a href={URL.createObjectURL(bankFile)} target="_blank" rel="noopener noreferrer" className="block">
                          {bankFile.type === 'application/pdf' ? (
                            <div className="w-20 h-24 rounded-lg border border-gray-200 bg-gray-50 flex flex-col items-center justify-center">
                              <FileText className="w-8 h-8 text-gray-400" /><span className="text-[10px] text-gray-400 mt-1">PDF</span>
                            </div>
                          ) : (
                            <img src={URL.createObjectURL(bankFile)} alt="Carátula bancaria" className="w-20 h-24 object-cover rounded-lg border border-gray-200 bg-gray-50" />
                          )}
                        </a>
                        <button type="button" onClick={() => setBankFile(null)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-100 hover:bg-red-200 text-red-500 rounded-full flex items-center justify-center transition-colors" title="Eliminar archivo">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <span className="text-xs text-gray-600 mt-1.5 max-w-[120px] truncate">{bankFile.name}</span>
                    </div>
                    <label className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Cambiar archivo
                      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={(e) => { const file = e.target.files?.[0]; if (file) setBankFile(file) }} />
                    </label>
                  </div>
                ) : existingBankCoverUrl ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Carátula bancaria (actual)</label>
                    <div className="inline-flex flex-col items-start">
                      <a href={existingBankCoverUrl} target="_blank" rel="noopener noreferrer" className="block">
                        {existingBankCoverUrl.toLowerCase().endsWith('.pdf') ? (
                          <div className="w-20 h-24 rounded-lg border border-gray-200 bg-gray-50 flex flex-col items-center justify-center">
                            <FileText className="w-8 h-8 text-gray-400" /><span className="text-[10px] text-gray-400 mt-1">PDF</span>
                          </div>
                        ) : (
                          <img src={existingBankCoverUrl} alt="Carátula bancaria" className="w-20 h-24 object-cover rounded-lg border border-gray-200 bg-gray-50" />
                        )}
                      </a>
                    </div>
                    <label className="inline-flex items-center gap-1.5 mt-3 text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Cambiar archivo
                      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={(e) => { const file = e.target.files?.[0]; if (file) setBankFile(file) }} />
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white">
                    <span className="text-sm font-medium text-gray-700">Adjuntar carátula <span className="text-gray-500 font-normal">(PDF, PNG, JPG)</span></span>
                    <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg cursor-pointer transition-colors text-sm font-medium">
                      <Upload className="w-4 h-4" /> Cargar
                      <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={(e) => { const file = e.target.files?.[0]; if (file) setBankFile(file) }} />
                    </label>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Documentos */}
          {currentStep === 4 && (
            <div>
              {docRows.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No hay documentos cargados para este proveedor</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Documento</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Archivo</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Fecha</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estatus</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Comentarios</th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {docRows.map((row) => {
                        const localDoc = localDocuments.find(ld => ld.documentTypeId === row.docTypeId)
                        const hasFile = !!(localDoc || row.existingDoc)
                        return (
                          <tr key={row.docTypeId} className="hover:bg-[#F4F7FB] transition-colors">
                            <td className="px-6 py-4">
                              <p className="text-sm font-medium text-gray-900">{row.docTypeName}</p>
                              {row.isRequired && <span className="text-xs text-red-500 font-medium">Requerido</span>}
                            </td>
                            <td className="px-6 py-4 text-sm">
                              {localDoc ? (
                                <div className="inline-flex items-center gap-1.5 text-blue-600">
                                  <FileText className="w-4 h-4 flex-shrink-0" />
                                  <span className="max-w-[140px] truncate">{localDoc.fileName}</span>
                                </div>
                              ) : row.existingDoc ? (
                                <a href={row.existingDoc.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 hover:underline">
                                  <FileText className="w-4 h-4 flex-shrink-0" />
                                  <span className="max-w-[140px] truncate">{row.existingDoc.fileName}</span>
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">Sin archivo</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              {row.existingDoc ? formatDate(row.existingDoc.uploadedAt) : '-'}
                            </td>
                            <td className="px-6 py-4">
                              {row.existingDoc ? getStatusBadge(row.existingDoc.status) : <span className="text-gray-400 text-xs">-</span>}
                            </td>
                            <td className="px-6 py-4">
                              {row.existingDoc ? (
                                <div className="flex items-center gap-2">
                                  <button onClick={() => handleOpenComment(row.existingDoc!.id)} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                                    <PlusCircle className="w-4 h-4" /><span>Agregar</span>
                                  </button>
                                  {row.existingDoc.hasComments && (
                                    <button onClick={() => handleOpenComment(row.existingDoc!.id)} className="inline-flex items-center gap-1 text-sm text-cyan-500 hover:text-cyan-700">
                                      <Eye className="w-4 h-4" /><span>Ver</span>
                                    </button>
                                  )}
                                </div>
                              ) : <span className="text-gray-400 text-xs">-</span>}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {hasFile ? (
                                  <>
                                    <label className="p-1.5 rounded hover:bg-blue-50 text-blue-600 cursor-pointer" title="Reemplazar archivo">
                                      <PencilLine className="w-4 h-4" />
                                      <input type="file" className="hidden" accept={row.allowedMimeTypes?.join(',') || '*'} onChange={handleFileInputChange(row.docTypeId, row.docTypeName)} />
                                    </label>
                                    {localDoc ? (
                                      <button onClick={() => handleLocalFileRemove(row.docTypeId)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Quitar archivo nuevo">
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    ) : (
                                      <label className="p-1.5 rounded hover:bg-red-50 text-red-600 cursor-pointer" title="Reemplazar archivo">
                                        <Trash2 className="w-4 h-4" />
                                        <input type="file" className="hidden" accept={row.allowedMimeTypes?.join(',') || '*'} onChange={handleFileInputChange(row.docTypeId, row.docTypeName)} />
                                      </label>
                                    )}
                                  </>
                                ) : (
                                  <label className="p-1.5 rounded hover:bg-blue-50 text-blue-600 cursor-pointer" title="Subir archivo">
                                    <Upload className="w-4 h-4" />
                                    <input type="file" className="hidden" accept={row.allowedMimeTypes?.join(',') || '*'} onChange={handleFileInputChange(row.docTypeId, row.docTypeName)} />
                                  </label>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Datos de acceso - OCULTO */}
        </div>

        {/* Saving progress overlay */}
        {isSaving && savingProgress && (
          <CapaDeModal>
            <div className="bg-white rounded-lg p-8 shadow-xl flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="text-gray-700 font-medium">{savingProgress}</p>
            </div>
          </CapaDeModal>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-6">
          <div />
          <div className="flex items-center gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                disabled={isSaving}
                className={estiloDeBoton('secundario')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                <span>Anterior</span>
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={isSaving}
              className={estiloDeBoton('primario')}
            >
              <span>{isSaving ? 'Guardando...' : (currentStep === 4 ? 'Guardar cambios' : 'Siguiente')}</span>
              {!isSaving && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              )}
            </button>
          </div>
        </div>

        {/* Confirm Modal */}
        <ConfirmActionModal
          isOpen={showConfirmModal}
          title="Guardar Cambios"
          message="¿Estás seguro de que deseas guardar los cambios realizados en el expediente del proveedor?"
          confirmText="Guardar"
          variante="primario"
          onConfirm={handleSave}
          onCancel={() => setShowConfirmModal(false)}
          isLoading={isSaving}
        />

        {/* Exit Confirmation Modal */}
        <ConfirmActionModal
          isOpen={showExitModal}
          title="¿Salir sin guardar?"
          message="Tienes cambios sin guardar. Si sales ahora, perderás las modificaciones realizadas."
          confirmText="Salir sin guardar"
          variante="peligro"
          onConfirm={() => { setShowExitModal(false); onBack() }}
          onCancel={() => setShowExitModal(false)}
          isLoading={false}
        />

        {/* Comments Modal */}
        {isCommentModalOpen && (
          <CapaDeModal>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Comentarios del documento</h3>
                </div>
                <button onClick={() => { setIsCommentModalOpen(false); setCommentingDocId(null); setCommentText('') }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Comments list */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {isLoadingComments ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-sm text-gray-500">Cargando comentarios...</p>
                  </div>
                ) : commentsHistory.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    <MessageSquare className="w-10 h-10 mx-auto mb-2" />
                    <p className="text-sm">No hay comentarios aún</p>
                  </div>
                ) : (
                  commentsHistory.map((c) => (
                    <div key={c.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-700">
                          {c.user?.firstName ? `${c.user.firstName} ${c.user.lastName || ''}`.trim() : c.user?.email || 'Usuario'}
                        </span>
                        <span className="text-xs text-gray-400">{formatDate(c.createdAt)}</span>
                      </div>
                      <p className="text-sm text-gray-600">{c.comment}</p>
                    </div>
                  ))
                )}
              </div>

              {/* New comment input */}
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveComment() } }}
                    placeholder="Escribe un comentario..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    disabled={isSavingComment}
                  />
                  <button
                    onClick={handleSaveComment}
                    disabled={isSavingComment || !commentText.trim()}
                    className={estiloDeBoton('primario')}
                  >
                    {isSavingComment ? '...' : 'Enviar'}
                  </button>
                </div>
              </div>
            </div>
          </CapaDeModal>
        )}
      </div>
    </>
  )
}

export default EditSupplierForm
